import styled from '@emotion/styled';
import Button from '@ep/components/Button';
import Container from '@ep/components/Container';
import theme from '@ep/theme';
import wordLibrary from '@ep/utils/topicsAndTags';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';

interface optionProps {
  short: string;
  value: string;
  label?: string;
}
interface menuProps {
  options: optionProps[];
  action: (option: string) => void;
  menuId?: string;
  initLabel?: string;
  disabled?: boolean;
}

const StyledContainer = styled(Container)`
  ${theme.mediaQueries.tablet} {
    width: 25rem;
  }
  ${theme.mediaQueries.mobile} {
  }
`;

const StyledButton = styled(Button)`
  color: ${theme.colors.oceanBlue};
  width: 28rem;
  text-align: left;
  text-transform: uppercase;
  border-radius: 0px;
  font-size: 1.5rem;
  padding: 1rem;
  height: 3rem;
  outline: none;
  border: 1px solid ${theme.colors.darkGray};
  background-color: rgba(0, 0, 0, 0);
  padding-right: 0rem;

  span {
    margin-right: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  svg {
    height: 3rem;
  }
  ${theme.mediaQueries.mobile} {
    padding-right: 0rem;
    font-size: 1rem;
    width: 15rem;
  }
`;

const StyledMenu = styled(Menu)<{getContentAnchorEl: null | string}>`
  width: 25rem;
  height: 30rem;
  ul.MuiList-root {
    border: 1px solid ${theme.colors.darkGray};
  }
  li.MuiMenuItem-root {
    white-space: inherit;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  width: 20rem;
  padding: 0px;
  padding-left: 1rem;
  padding-right: 1rem;
  margin-top: -1rem;
  margin-bottom: 1rem;
`;

const StyledP = styled('p')`
  line-height: 1.8rem;
  width: 20rem;
  margin-top: 1rem;
  margin-bottom: 0px;
  padding-bottom: 1rem;
  color: ${theme.colors.darkGray};
  border-bottom: 1px solid ${theme.colors.darkGray};
  font-family: ${theme.typography.primaryFont.regular};
  font-size: 1.4rem;
`;

const ContactFormMenu = ({
  options,
  action,
  menuId,
  initLabel,
  disabled,
}: menuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [useInitLabel, setUseInitLabel] = React.useState(true);

  const [select, setSelect] =
    useInitLabel && initLabel
      ? React.useState(initLabel)
      : React.useState('Select');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const resortOptions = (options: optionProps[]) => {
    let startOption;
    let optionsList;

    let returnArr = options;
    if (options[0].value === 'All Topics') {
      startOption = options.slice(0, 1);
      optionsList = options.slice(1);
      optionsList.sort(function(a, b) {
        return a.short.localeCompare(b.short);
      });
      returnArr = startOption.concat(optionsList);
    }
    return returnArr;
  };

  function handleSelect(option: optionProps, value: string) {
    setUseInitLabel(false);
    handleClose();
    const selected = option.label ? option.label : option.short;
    setSelect(selected);
    action(value);
  }

  const data = useStaticQuery(graphql`
    query {
      gcms {
        menu(where: { position: "Sales" }) {
          data
        }
      }
    }
  `);

  const menuData = data.gcms.menu.data;

  const sortedOptions = resortOptions(options);

  return (
    <StyledContainer>
      <StyledButton
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleClick}
        color={`gray`}
        label={select.replace(/_/g, ' ')}
        id={menuId}
        aria-label="Menu"
        rightIcon={`ChevronBlue`}
        disabled={disabled}
      >
        {`Select`}
      </StyledButton>
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {sortedOptions.map(option => {
          let labelPretty =
            wordLibrary[option.short as keyof typeof wordLibrary];

          return (
            <StyledMenuItem
              key={option.short}
              onClick={() => handleSelect(option, option.value)}
            >
              <StyledP>
                {labelPretty
                  ? labelPretty
                  : option.label
                  ? option.label
                  : option.short.replace(/_/g, ' ')}
              </StyledP>
            </StyledMenuItem>
          );
        })}
      </StyledMenu>
    </StyledContainer>
  );
};

export default ContactFormMenu;
